import React, { useState, useMemo } from 'react';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, subDays, startOfDay, endOfDay, startOfMonth, parseISO } from 'date-fns';

interface DateRangePickerProps {
  from: string;
  to: string;
  onChange: (from: string, to: string) => void;
}

type PresetKey = 'today' | 'yesterday' | 'last7' | 'last30' | 'thisMonth';

interface Preset {
  key: PresetKey;
  label: string;
  getRange: () => { from: Date; to: Date };
}

const presets: Preset[] = [
  {
    key: 'today',
    label: 'Today',
    getRange: () => {
      const now = new Date();
      return { from: startOfDay(now), to: endOfDay(now) };
    },
  },
  {
    key: 'yesterday',
    label: 'Yesterday',
    getRange: () => {
      const yesterday = subDays(new Date(), 1);
      return { from: startOfDay(yesterday), to: endOfDay(yesterday) };
    },
  },
  {
    key: 'last7',
    label: 'Last 7 Days',
    getRange: () => {
      const now = new Date();
      return { from: startOfDay(subDays(now, 6)), to: endOfDay(now) };
    },
  },
  {
    key: 'last30',
    label: 'Last 30 Days',
    getRange: () => {
      const now = new Date();
      return { from: startOfDay(subDays(now, 29)), to: endOfDay(now) };
    },
  },
  {
    key: 'thisMonth',
    label: 'This Month',
    getRange: () => {
      const now = new Date();
      return { from: startOfMonth(now), to: endOfDay(now) };
    },
  },
];

const fmt = (d: Date) => format(d, 'yyyy-MM-dd');

const DateRangePicker: React.FC<DateRangePickerProps> = ({ from, to, onChange }) => {
  const [activePreset, setActivePreset] = useState<PresetKey | null>('last7');

  const fromDate = useMemo(() => (from ? parseISO(from) : null), [from]);
  const toDate = useMemo(() => (to ? parseISO(to) : null), [to]);

  const handlePresetClick = (preset: Preset) => {
    const range = preset.getRange();
    setActivePreset(preset.key);
    onChange(fmt(range.from), fmt(range.to));
  };

  const handleFromChange = (date: Date | null) => {
    if (!date) return;
    setActivePreset(null);
    onChange(fmt(date), to);
  };

  const handleToChange = (date: Date | null) => {
    if (!date) return;
    setActivePreset(null);
    onChange(from, fmt(date));
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Stack
        direction="row"
        spacing={1.5}
        sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1 }}
      >
        <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
          {presets.map((preset) => (
            <Chip
              key={preset.key}
              label={preset.label}
              size="small"
              variant={activePreset === preset.key ? 'filled' : 'outlined'}
              color={activePreset === preset.key ? 'primary' : 'default'}
              onClick={() => handlePresetClick(preset)}
              sx={{
                fontWeight: activePreset === preset.key ? 600 : 400,
                cursor: 'pointer',
              }}
            />
          ))}
        </Stack>

        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <DatePicker
            label="From"
            value={fromDate}
            onChange={handleFromChange}
            slotProps={{
              textField: { size: 'small', sx: { width: 160 } },
            }}
            maxDate={toDate ?? undefined}
          />
          <DatePicker
            label="To"
            value={toDate}
            onChange={handleToChange}
            slotProps={{
              textField: { size: 'small', sx: { width: 160 } },
            }}
            minDate={fromDate ?? undefined}
          />
        </Stack>
      </Stack>
    </LocalizationProvider>
  );
};

export default DateRangePicker;
